import { getHighAccuracyLocation } from "../services/UserTracking";

/**
 * Detect if the current device is a mobile phone/tablet browser.
 * Checks both user agent and touch capability.
 */
export const isMobileDevice = () => {
  const ua = navigator.userAgent || "";
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i;
  const hasTouchScreen = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  return mobileRegex.test(ua) && hasTouchScreen;
};

/**
 * Get the device type string for backend recording.
 */
export const getDeviceType = () => {
  return isMobileDevice() ? "mobile" : "desktop";
};

/**
 * Get location from the browser's native Geolocation API (uses phone GPS hardware).
 * Returns { lat, lng, accuracy } or throws on failure.
 */
const getBrowserGPSLocation = (timeoutMs = 15000) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error("Geolocation not supported"));
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (err) => {
        reject(err);
      },
      {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 0,
      }
    );
  });
};

/**
 * Get location from the server-side Google Geolocation API (IP-based).
 * Used as fallback for desktop or when GPS is denied.
 * Returns { lat, lng, accuracy } or null.
 */
const getServerIPLocation = async () => {
  const result = await getHighAccuracyLocation();
  if (result?.locations) {
    return {
      lat: result.locations.lat,
      lng: result.locations.lng,
      accuracy: result.locations.accuracy || null,
    };
  }
  return null;
};

/**
 * Smart location resolver:
 * - Mobile → browser GPS (hardware), falls back to server IP
 * - Desktop → browser GPS (WiFi-based positioning), falls back to server IP
 *
 * Returns { lat, lng, accuracy, source: 'gps'|'ip' } or null if both fail.
 */
export const getDeviceLocation = async () => {
  const mobile = isMobileDevice();

  // Both mobile and desktop: prefer browser GPS first.
  // On desktop, browsers use WiFi/network positioning which is far more accurate
  // than IP geolocation (IP can be off by miles; WiFi positioning is typically
  // within 50-100m). IP geolocation is only used when the user denies location
  // permission or the browser cannot determine position.
  try {
    const timeoutMs = mobile ? 15000 : 10000;
    const loc = await getBrowserGPSLocation(timeoutMs);
    return { ...loc, source: "gps" };
  } catch {
    // GPS denied or unavailable — fall back to server IP
    try {
      const loc = await getServerIPLocation();
      return loc ? { ...loc, source: "ip" } : null;
    } catch {
      return null;
    }
  }
};
